import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./BannerCarousel.css";

const BannerCarousel = ({
    imageList = [],
    mobileImageList = [],
    bannerLinks = [],
    autoSlide = true,
    intervalTime = 5000,
}) => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const list = isMobile ? mobileImageList : imageList;
    const numSlides = list.length;

    // Infinite loop: [last, ...all, first] so we can animate seamlessly
    const extendedList = numSlides > 1
        ? [list[numSlides - 1], ...list, list[0]]
        : list;

    // Index over extended list: 1 to numSlides = real slides; 0 = duplicate last; numSlides+1 = duplicate first
    const [index, setIndex] = useState(numSlides > 1 ? 1 : 0);
    const [transitionDuration, setTransitionDuration] = useState(0.5);

    // Resize detection
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // When we land on a duplicate slide (0 or numSlides+1), reset position instantly so the loop is seamless (0 or numSlides+1), instantly reset so the loop is seamless
    useEffect(() => {
        if (numSlides <= 1) return;
        if (index === 0) {
            setTransitionDuration(0);
            setIndex(numSlides);
        } else if (index === numSlides + 1) {
            setTransitionDuration(0);
            setIndex(1);
        } else {
            setTransitionDuration(0.5);
        }
    }, [index, numSlides]);

    // Auto slide: move one by one, then loop (animate to duplicate first, then reset)
    useEffect(() => {
        if (!autoSlide || numSlides <= 1) return;
        const timer = setInterval(() => {
            setIndex((prev) => (prev > numSlides ? 1 : prev + 1));
        }, intervalTime);
        return () => clearInterval(timer);
    }, [autoSlide, intervalTime, numSlides]);

    const goNext = () => {
        if (numSlides <= 1) return;
        setIndex((prev) => (prev > numSlides ? 1 : prev + 1));
    };

    const goPrev = () => {
        if (numSlides <= 1) return;
        setIndex((prev) => (prev <= 0 ? numSlides : prev - 1));
    };

    // Real slide index for dots and navigation (0 to numSlides-1)
    const realIndex = index === 0 ? numSlides - 1 : index === numSlides + 1 ? 0 : index - 1;

    const getBannerUrl = (link) => {
        if (!link) return "/products";
        return `/products?${link.filter}=${encodeURIComponent(link.value)}`;
    };

    const onBannerClick = () => {
        const link = bannerLinks[realIndex];
        navigate(getBannerUrl(link));
    };

    if (!list.length) return null;

    const slideWidthPercent = extendedList.length > 0 ? 100 / extendedList.length : 100;

    return (
        <div className="home-carousel">
            <button className="carousel-arrow lefts" onClick={goPrev}>
                ❮
            </button>
            <button className="carousel-arrow rights" onClick={goNext}>
                ❯
            </button>

            <motion.div
                className="carousel-track carousel-track-infinite"
                style={{ width: `${extendedList.length * 100}%` }}
                animate={{ x: `-${index * slideWidthPercent}%` }}
                transition={{ duration: transitionDuration, ease: "easeInOut" }}
                drag="x"
                dragElastic={0.2}
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={(_, info) => {
                    const swipeThreshold = 50;
                    if (numSlides <= 1) return;
                    if (info.offset.x < -swipeThreshold) {
                        setIndex((prev) => (prev > numSlides ? 1 : prev + 1));
                    } else if (info.offset.x > swipeThreshold) {
                        setIndex((prev) => (prev <= 0 ? numSlides : prev - 1));
                    }
                }}
            >
                {extendedList.map((img, i) => (
                    <div
                        className="carousel-slide"
                        key={i}
                        style={{ flex: `0 0 ${slideWidthPercent}%` }}
                        onClick={onBannerClick}
                    >
                        <img
                            src={img}
                            alt=""
                            className="carousel-image"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = list[0] || "";
                            }}
                        />
                    </div>
                ))}
            </motion.div>

            <div className="carousel-dots">
                {list.map((_, i) => (
                    <span
                        key={i}
                        className={`dot ${i === realIndex ? "active" : ""}`}
                        onClick={() => setIndex(i + 1)}
                    />
                ))}
            </div>
        </div>
    );
};

export default BannerCarousel;